import { Component, Inject, OnDestroy } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material';
import { MatDialog } from '@angular/material/dialog';
import { HttpParams } from '@angular/common/http';
import { TranslateService } from '@ngx-translate/core';
import { finalize, startWith, takeUntil } from 'rxjs/operators';
import { BehaviorSubject, forkJoin, ReplaySubject } from 'rxjs';
import { Store } from '@ngrx/store';

import { ProductTypes } from 'common/src/modules/products/product-types';
import {
  GlAccountRuleModel,
  VatDictionaryItemModel
} from 'projects/workspace/src/app/accounting/accounting-settings-module/models';
import { CompositeProductModel, FamilyModel, ProductsService } from '../../../products';
import { GlAccountsApiService } from 'projects/workspace/src/app/gl-accounts/services/gl-accounts-api.service';
import { AddVatRateModalComponent, CommonModalsActionsEnum, DangerModalComponent } from '../../modals-common';
import { selectCompanyProfile } from 'projects/workspace/src/app/administration/store/selectors';
import { CompanyProfile } from 'projects/workspace/src/app/administration/models/company-profile.model';
import { AppState } from 'projects/workspace/src/app/store/state/app.state';
import { VatDictionaryService } from 'projects/workspace/src/app/accounting/accounting-settings-module/services';
import { formatVatRateLabel } from '../../../rnpl-common/helpers';


@Component({
  selector: 'rnpl-gl-allocation-rule-modal',
  templateUrl: './gl-allocation-rule-modal.component.html'
})
export class GlAllocationRuleModalComponent implements OnDestroy {

  public form: FormGroup;
  public productTypes = ProductTypes;
  public vatList: {value: number; label: string}[] = [];
  public hideSelectedVatRate: boolean = false;
  public legalCountryIso3: string;
  public isSmallBusiness: boolean;
  public productTypesList: {value: ProductTypes; label: string}[] = [
    {value: ProductTypes.GOODS, label: 'COMMON.GOODS'},
    {value: ProductTypes.SERVICES, label: 'APP.SERVICES'},
    {value: ProductTypes.DIGITAL, label: 'APP.DIGITAL_PRODUCTS'},
    {value: ProductTypes.BUNDLE, label: 'BUNDLES.BUNDLES'},
  ];
  public flatFamilies: FamilyModel[] = [];
  // public generalCategories: FamilyModel[] = [];
  // public generalProducts: any[] = [];
  // public generalProductsSource: any[] = [];

  readonly destroy$: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);
  readonly loadingFamilies$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  // readonly loadingGeneralCategories$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  // readonly loadingGeneralProducts$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  readonly submitRequest$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  readonly deleteRequest$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  constructor(
    private store: Store<AppState>,
    private readonly fb: FormBuilder,
    private readonly dialog: MatDialog,
    private readonly translate: TranslateService,
    private readonly productsService: ProductsService,
    private readonly glAccountsApiService: GlAccountsApiService,
    private readonly vatDictionaryService: VatDictionaryService,
    public readonly dialogRef: MatDialogRef<GlAllocationRuleModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: {
      rule: GlAccountRuleModel,
      predefinedOptions: Partial<GlAccountRuleModel>,
    }
  ) {
    this.dialogRef.keydownEvents().subscribe(event => {
      if (event.key === 'Escape') {
        this.dialogRef.close();
      }
    });

    this.initForm();

    this.store.select(selectCompanyProfile)
      .pipe(takeUntil(this.destroy$))
      .subscribe((companyProfile: CompanyProfile) => {
        this.legalCountryIso3 = companyProfile.legalAddress.country_iso3;
        this.isSmallBusiness = companyProfile.isSmallBusiness;
        this.getRates(this.legalCountryIso3);
      });

    if (this.data) {
      if (this.data.rule) {
        this.form.patchValue(this.data.rule);
        this.reverseChargeChanged();
      }
      if (this.data.predefinedOptions) { this.form.patchValue(this.data.predefinedOptions); }
      if (this.data.rule || this.data.predefinedOptions) {
        // get lists if needed
        this.getLists();
      }
    }
  }

  public getRates(legalCountryIso3): void {
    forkJoin([
      this.vatDictionaryService.getVatDictionaryRates([legalCountryIso3]),
      this.vatDictionaryService.getVatDictionary([legalCountryIso3]),
    ])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([list, dictionaryItems]) => {
        const rates = [...list];
        const controlValue = this.vatRateControl.value;
        const currentRate = typeof controlValue === 'string'
          ? Number(controlValue.replace(',', '.'))
          : Number(controlValue);
        const hasCurrentRate = controlValue !== null
          && controlValue !== undefined
          && controlValue !== ''
          && currentRate !== -1
          && !isNaN(currentRate);
        const currentRateExists = rates.some(rate => +rate === currentRate);

        this.hideSelectedVatRate = hasCurrentRate && !currentRateExists;

        if (this.hideSelectedVatRate) {
          const zeroRateIndex = rates.findIndex(rate => +rate === 0);
          rates.splice(zeroRateIndex < 0 ? rates.length : zeroRateIndex, 0, currentRate);
        }

        this.vatList = rates.map(i => ({
          value: i,
          label: formatVatRateLabel(i)
        }));
        this.vatList.push({value: -1, label: this.translate.instant('GL_ACCOUNTS.ALL_TAX_RATES')});

        if (!this.hasVatRateValue()) {
          const primaryRate = dictionaryItems.find(item => item.primary);
          this.vatRateControl.patchValue(this.isSmallBusiness ? 0 : primaryRate && primaryRate.rate);
        }
      });
  }

  private hasVatRateValue(): boolean {
    const value = this.vatRateControl.value;
    return value !== null && value !== undefined && value !== '';
  }

  public addRate(): void {
    this.dialog.open(AddVatRateModalComponent, {
      data: {
        countryIso3: this.legalCountryIso3
      },
      backdropClass: 'dragged-modal-type',
      disableClose: true,
      minWidth: `${336}px`,
      maxWidth: `${336}px`,
      panelClass: ['dragged-panel', 'sm-modal'],
      position: {}
    }).afterClosed().subscribe((result: VatDictionaryItemModel) => {
      if (!result) { return; }

      this.vatRateControl.patchValue(result.rate);
      this.getRates(this.legalCountryIso3);
    });
  }

  private initForm(): void {
    this.form = this.fb.group({
      productType: [null, [Validators.required]],
      location: [null, [Validators.required]],
      vatRate: [null, [Validators.required]],
      reverseCharge: [false],
      glAccountId: [null, [Validators.required]],
      detailedOptions: this.fb.group({
        enabled: [false],
        type: [],
        entityId: [],
        entityName: [],
      }),
    });

    this.productTypeControl.valueChanges
      .pipe(
        startWith(this.productTypeControl.value),
        takeUntil(this.destroy$)
      )
      .subscribe((value) => {
        if (value) {
          this.detailedOptionsEnableControl.enable();
        } else {
          this.detailedOptionsEnableControl.disable();
        }
      });

    this.detailedOptionsEnableControl.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((value: boolean) => {
        this.detailedOptionsEntityIdControl.updateValueAndValidity();
        this.detailedOptionsTypeControl.updateValueAndValidity();

        if (value) {
          this.detailedOptionsEntityIdControl.setValidators(Validators.required);
          this.detailedOptionsTypeControl.setValidators(Validators.required);
        } else {
          this.detailedOptionsEntityIdControl.clearValidators();
          this.detailedOptionsTypeControl.clearValidators();
        }
      });
  }

  public setName(name: string): void {
    this.detailedOptionsEntityNameControl.patchValue(name);
  }

  public setGeneralProductName(product: CompositeProductModel): void {
    const name = `${this.translate.instant('GL_CATEGORY.' + product.category)}/${product.name}`;

    this.setName(name);
  }

  public reverseChargeChanged(): void {
    if (this.reverseChargeControl.value) {
      this.vatRateControl.patchValue(0);
      this.vatRateControl.disable();
    } else {
      this.vatRateControl.enable();
    }
  }

  public vatChanged(): void {
    const location = this.locationControl.value;
    if (this.vatRateControl.value === 0) {
      // this.vatRateControl.disable();
      if (location === 'EU' || location === 'NON_EU') {
        this.vatRateControl.disable();
        this.reverseChargeControl.patchValue(true);
      }
    }
  }

  public productTypeChanged(): void {
    this.detailedOptionsTypeControl.patchValue(null);
    this.detailedOptionsEntityIdControl.patchValue(null);
    this.detailedOptionsEntityNameControl.patchValue(null);
    this.detailedTypeChanged();

    if (this.productTypeControl.value === ProductTypes.BUNDLE) {
      this.detailedOptionsTypeControl.disable();
    } else {
      this.detailedOptionsTypeControl.enable();
    }

    this.form.markAsUntouched();
  }

  public detailedTypeChanged(): void {
    this.detailedOptionsEntityIdControl.patchValue(null);
    this.detailedOptionsEntityNameControl.patchValue(null);
    this.getLists();
  }

  private getLists(): void {
    if (this.detailedOptionsTypeControl.value === 'PRODUCT_CATEGORY') {
      this.getFamiliesTree();
    }
    // if (this.detailedOptionsTypeControl.value === 'GENERAL_CATEGORY') {
    //   if (!this.generalCategories.length) {
    //     this.getGeneralCategoriesTree();
    //   }
    // }
    // if (this.detailedOptionsTypeControl.value === 'GENERAL_PRODUCT') {
    //   this.getGeneralProducts();
    // }
  }

  // private getGeneralProducts(): void {
  //   if (this.generalProductsSource.length) {
  //     this.filterGeneralProducts();
  //     return;
  //   }
  //   this.loadingFamilies$.next(true);
  //
  //   this.productsService.getGeneralProductsListDropdown(false)
  //     .pipe(takeUntil(this.destroy$))
  //     .subscribe((response) => {
  //       this.generalProductsSource = response;
  //       this.filterGeneralProducts();
  //       this.loadingFamilies$.next(false);
  //     });
  // }
  //
  // private filterGeneralProducts(): void {
  //   if (!this.generalProductsSource.length || !this.productTypeControl.value) { return; }
  //   this.generalProducts = this.generalProductsSource.filter(glProduct => glProduct.entityForm === this.productTypeControl.value);
  // }

  private getFamiliesTree(): void {
    if (!this.productTypeControl.value) { return; }
    this.loadingFamilies$.next(true);
    this.flatFamilies = [];

    const requestParams = new HttpParams().set('products_type', this.productTypeControl.value.replace('-', '_'));

    this.productsService.getFamiliesTree(requestParams)
      .pipe(takeUntil(this.destroy$))
      .subscribe(response => {
        this.flatFamilies =  this.convertTreeToFlat(response.data);
        this.loadingFamilies$.next(false);
      });
  }

  // private getGeneralCategoriesTree(): void {
  //   this.loadingGeneralCategories$.next(true);
  //
  //   const requestParams = new HttpParams().set('products_type', 'general');
  //
  //   this.productsService.getFamiliesTree(requestParams)
  //     .pipe(takeUntil(this.destroy$))
  //     .subscribe(response => {
  //       this.generalCategories = response.data.map(itm => ({
  //         ...itm,
  //         translatedTitle: this.translate.instant('GL_CATEGORY.' + itm.title)
  //       }));
  //
  //       this.loadingGeneralCategories$.next(false);
  //     });
  // }

  private convertTreeToFlat(tree: FamilyModel[]): FamilyModel[] {
    if (!tree) { return; }

    const flatArray = [];
    const getTreeItems = (branch) => {
      branch.map(node => {
        if (!node.product_locked_at) {
          flatArray.push(node);
        }

        if (node.children.length) {
          getTreeItems(node.children);
        }
      });
    };

    getTreeItems(tree);

    return flatArray.map(itm => ({
      ...itm,
      id: +itm.id
    }));
  }

  public submitCreate(): void {
    this.form.markAllAsTouched();
    this.form.updateValueAndValidity();

    if (this.form.invalid) { return; }

    this.submitRequest$.next(true);

    this.glAccountsApiService.createGlAccountRule(this.form.getRawValue())
      .pipe(
        finalize(() => this.submitRequest$.next(false)),
        takeUntil(this.destroy$)
      ).subscribe(() => this.dialogRef.close(true));
  }

  public submitUpdate(): void {
    this.form.markAllAsTouched();
    this.form.updateValueAndValidity();

    if (this.form.invalid) { return; }

    this.submitRequest$.next(true);

    this.glAccountsApiService.updateGlAccountRule(this.data.rule.ruleId, this.form.getRawValue())
      .pipe(
        finalize(() => this.submitRequest$.next(false)),
        takeUntil(this.destroy$)
      ).subscribe(() => this.dialogRef.close(true));
  }

  public deleteRule(): void {
    if (this.deleteRequest$.getValue()) { return; }

    this.dialog.open(DangerModalComponent, {
      data: {
        title: 'GL_ACCOUNTS.DELETE_RULE_TITLE',
        message: 'GL_ACCOUNTS.DELETE_RULE_MSG',
        confirmBtnText: 'BUTTON.DELETE',
        confirmBtnIcon: 'trash-2'
      }
    }).afterClosed().subscribe((res: CommonModalsActionsEnum) => {
      if (res === CommonModalsActionsEnum.CONFIRM) {
        this.deleteRequest$.next(true);

        this.glAccountsApiService.deleteGlAccountRule(this.data.rule.ruleId)
          .pipe(
            finalize(() => this.deleteRequest$.next(false)),
            takeUntil(this.destroy$)
          )
          .subscribe(() => this.dialogRef.close(true));
      }
    });
  }

  get productTypeControl(): FormControl { return this.form.get('productType') as FormControl; }
  get locationControl(): FormControl { return this.form.get('location') as FormControl; }
  get vatRateControl(): FormControl { return this.form.get('vatRate') as FormControl; }
  get reverseChargeControl(): FormControl { return this.form.get('reverseCharge') as FormControl; }
  get glAccountIdControl(): FormControl { return this.form.get('glAccountId') as FormControl; }
  get detailedOptionsEnableControl(): FormControl { return this.form.get('detailedOptions').get('enabled') as FormControl; }
  get detailedOptionsTypeControl(): FormControl { return this.form.get('detailedOptions').get('type') as FormControl; }
  get detailedOptionsEntityIdControl(): FormControl { return this.form.get('detailedOptions').get('entityId') as FormControl; }
  get detailedOptionsEntityNameControl(): FormControl { return this.form.get('detailedOptions').get('entityName') as FormControl; }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
  }

}
